import moment from "moment";
import { construirPlan, COLUMNAS_POR_MATRIZ, valoresPorMatriz, encabezadosPorEtapa, valoresPorEtapa, totalPorMatriz, COLUMNAS_PLAN, COLUMNAS_CUMPLIMIENTO, filasResumen, textoAtraso, horaCorta, SIN_ORDENES } from "../Pages/Panel/PresupuestosPU/OrdenesDeProduccionPU/PlaneacionProduccionPU/planProduccion.utils";

const TINTA = "#1A3A4A";
const GRIS = "#8C8C8C";
const PETROLEO = "#006B7A";
const CIAN = "#00BFD6";
const FONDO_DIA = "#FFF2CC";
const FONDO_TOTAL = "#EDEDED";
const ANCHO = 752;

const texto = (valor, extra = {}) => ({ text: valor === null || valor === undefined ? "" : String(valor), style: "celda", ...extra });

const ANCHO_DIA = 16;

const diaVertical = (dia) => {
  const alto = Math.max(70, dia.filas.length * 26);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${ANCHO_DIA}" height="${alto}" viewBox="0 0 ${ANCHO_DIA} ${alto}"><text transform="translate(11 ${alto / 2}) rotate(-90)" font-size="7.5" font-weight="bold" fill="${TINTA}" text-anchor="middle">${dia.etiqueta}</text></svg>`;
};

const planProduccionPdf = ({ grupos, desde, hasta, empresa = {}, etiquetaRango, usuarioNombre }) => {
  const { categorias, dias, resumen, porMatriz, etapasTodas } = construirPlan(grupos, { desde, hasta });
  const encabezados = [...COLUMNAS_PLAN.map((c) => c.titulo), ...categorias, ...COLUMNAS_CUMPLIMIENTO.map((c) => c.titulo)];
  const widths = [ANCHO_DIA + 4, 28, "*", 58, 26, 30, 30, 28, 26, 30, 30, 28, 40, ...categorias.map(() => 34), 30, 30, 30];

  const body = [encabezados.map((t) => ({ text: t, style: "encabezado" }))];
  dias.forEach((dia) => {
    if (dia.filas.length === 0) {
      body.push([
        {
          text: [{ text: `${dia.etiqueta}   `, bold: true }, { text: SIN_ORDENES, color: GRIS }],
          style: "celda",
          alignment: "left",
          fillColor: FONDO_DIA,
          colSpan: encabezados.length,
        },
        ...Array.from({ length: encabezados.length - 1 }, () => ({})),
      ]);
      return;
    }
    dia.filas.forEach((fila, indice) => {
      const base = fila.inicioPlan;
      body.push([
        indice === 0 ? { svg: diaVertical(dia), alignment: "center", fillColor: FONDO_DIA, rowSpan: dia.filas.length } : {},
        texto(fila.folio),
        texto(fila.descripcion, { alignment: "left" }),
        texto(fila.noParte),
        texto(horaCorta(fila.inicioPlan)),
        texto(horaCorta(fila.finPlan, base)),
        texto(fila.cantidad.toLocaleString("es-MX")),
        texto(fila.piezasPorHora ?? ""),
        texto(fila.horasPlan),
        texto(horaCorta(fila.inicioReal, base)),
        texto(horaCorta(fila.finReal, base)),
        texto(fila.horasReal ?? ""),
        texto(fila.estado),
        ...categorias.map((c) => texto(fila.piezasPorCategoria[c] || 0)),
        texto(fila.faltante),
        texto(`${fila.pctPiezas}%`),
        texto(textoAtraso(fila), fila.atrasada ? { color: "#C00000", bold: true } : {}),
      ]);
    });
    const total = (valor, extra = {}) => texto(valor, { bold: true, fillColor: FONDO_TOTAL, ...extra });
    body.push([
      total(""),
      total(""),
      total(`TOTAL ${dia.etiqueta}`, { alignment: "left" }),
      total(""),
      total(""),
      total(""),
      total(dia.cantidad.toLocaleString("es-MX")),
      total(""),
      total(dia.horasPlan),
      total(""),
      total(""),
      total(dia.horasReal || ""),
      total(""),
      ...categorias.map((c) => total(dia.filas.reduce((acc, f) => acc + (f.piezasPorCategoria[c] || 0), 0))),
      total(dia.faltante),
      total(`${dia.pctPiezas}%`),
      total(dia.ordenesAtrasadas || ""),
    ]);
  });
  const tablaPorMatriz = {
    margin: [0, 12, 0, 0],
    unbreakable: porMatriz.length <= 15,
    stack: [
      { text: "CUMPLIMIENTO POR MATRIZ", fontSize: 10, bold: true, color: PETROLEO, margin: [0, 0, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: [70, "*", 36, 44, 52, 36, 44, 40, 52, 52, 52],
          body: [
            COLUMNAS_POR_MATRIZ.map((c) => ({ text: c.titulo, style: "encabezado" })),
            ...porMatriz.map((m) =>
              valoresPorMatriz(m).map((v, i) =>
                texto(v, i === 1 ? { alignment: "left" } : i === 8 ? { bold: true, color: m.pctCumplimiento < 50 ? "#C00000" : m.pctCumplimiento < 100 ? "#9C5700" : TINTA } : {})
              )
            ),
          ],
        },
        layout: { hLineColor: () => "#adaaaa", vLineColor: () => "#adaaaa", fillColor: (fila) => (fila === 0 ? "#C5E3F6" : null), paddingLeft: () => 2, paddingRight: () => 2 },
      },
    ],
  };

  const totalMatrices = totalPorMatriz(porMatriz, etapasTodas);
  const tablaPorEtapa = {
    margin: [0, 12, 0, 0],
    unbreakable: porMatriz.length <= 15,
    stack: [
      { text: "PIEZAS POR ETAPA Y MATRIZ (dónde está cada pieza hoy)", fontSize: 10, bold: true, color: PETROLEO, margin: [0, 0, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: [60, "*", 40, ...etapasTodas.map(() => 38), 40, 48],
          body: [
            encabezadosPorEtapa(etapasTodas).map((t) => ({ text: t, style: "encabezado" })),
            ...porMatriz.map((m) => valoresPorEtapa(m, etapasTodas).map((v, i) => texto(v, i === 1 ? { alignment: "left" } : {}))),
            valoresPorEtapa(totalMatrices, etapasTodas).map((v, i) => texto(v, { bold: true, fillColor: FONDO_TOTAL, ...(i === 1 ? { alignment: "left" } : {}) })),
          ],
        },
        layout: { hLineColor: () => "#adaaaa", vLineColor: () => "#adaaaa", fillColor: (fila) => (fila === 0 ? "#C5E3F6" : null), paddingLeft: () => 2, paddingRight: () => 2 },
      },
    ],
  };

  const tablaResumen = {
    margin: [0, 10, 0, 0],
    unbreakable: true,
    table: {
      widths: [160, 70],
      body: [
        [{ text: "RESUMEN DEL PERIODO", style: "encabezado", colSpan: 2 }, {}],
        ...filasResumen(resumen).map(([etiqueta, valor]) => [
          { text: etiqueta, style: "celda", alignment: "left", fillColor: FONDO_TOTAL },
          { text: String(valor), style: "celda", bold: true, fillColor: FONDO_TOTAL },
        ]),
      ],
    },
    layout: { hLineColor: () => "#adaaaa", vLineColor: () => "#adaaaa", fillColor: (fila) => (fila === 0 ? "#C5E3F6" : null) },
  };

  return {
    pageOrientation: "landscape",
    pageSize: "LETTER",
    pageMargins: [20, 20, 20, 28],
    footer: (actual, totalPaginas) => ({
      columns: [
        { text: `Imprimió: ${usuarioNombre || "—"}`, fontSize: 7, color: GRIS, margin: [20, 6, 0, 0] },
        { text: `${actual} / ${totalPaginas}`, alignment: "right", fontSize: 7, color: GRIS, margin: [0, 6, 20, 0] },
      ],
    }),
    content: [
      {
        columns: [
          empresa.logo?.base64Completa ? { image: "logo", fit: [64, 64], width: 80 } : { text: "", width: 80 },
          {
            width: "*",
            stack: [
              { text: empresa.nombre || "-", fontSize: 16, bold: true, color: TINTA, alignment: "center" },
              { text: "PLAN DE PRODUCCIÓN", fontSize: 13, bold: true, color: PETROLEO, alignment: "center", margin: [0, 4, 0, 0] },
              { text: etiquetaRango || "", fontSize: 9, color: GRIS, alignment: "center" },
            ],
          },
          {
            width: 120,
            stack: [
              { text: "MPP-FT-06", fontSize: 10, bold: true, alignment: "right" },
              { text: moment().format("DD/MM/YYYY HH:mm"), fontSize: 8, color: GRIS, alignment: "right" },
            ],
          },
        ],
      },
      { canvas: [{ type: "line", x1: 0, y1: 4, x2: ANCHO, y2: 4, lineWidth: 2, lineColor: CIAN }], margin: [0, 4, 0, 8] },
      dias.length
        ? {
            table: { headerRows: 1, widths, body },
            layout: { hLineColor: () => "#adaaaa", vLineColor: () => "#adaaaa", fillColor: (fila) => (fila === 0 ? "#C5E3F6" : null), paddingLeft: () => 2, paddingRight: () => 2 },
          }
        : { text: "No hay órdenes planeadas en este periodo.", color: GRIS },
      ...(dias.some((d) => d.filas.length > 0) ? [tablaPorMatriz, tablaPorEtapa, tablaResumen] : []),
    ],
    images: empresa.logo?.base64Completa ? { logo: empresa.logo.base64Completa } : {},
    defaultStyle: { font: "Roboto", fontSize: 7 },
    styles: {
      encabezado: { fontSize: 6.5, bold: true, alignment: "center" },
      celda: { fontSize: 7, alignment: "center" },
    },
  };
};

export default planProduccionPdf;
